import * as Sentry from "@sentry/react";

const ignoredErrors = [
  // This error occurs because we use a form inside of a dialog. The dialog is created through
  // a portal so there is no form inside of another form. But the submit event is dispatched by
  // the form inside of the dialog and bubbled to the form inside of the page.
  // https://github.com/edmundhung/conform/discussions/590
  /The submit event is dispatched by form#.+ instead of form#.+/,

  // This error occurs when the user cancels an upload, not a bug.
  "Operation cancelled by caller.",

  // This error appears from time to time since updating to react-router v7.5, no idea why.
  // It seems to be a bug in react-router, but it doesn't affect the app.
  "Cannot read properties of undefined (reading 'routes/",
];

function isIgnoredError(error: unknown): boolean {
  if (error instanceof Error) {
    return ignoredErrors.some((ignoredError) => {
      if (typeof ignoredError === "string") {
        return error.message.includes(ignoredError);
      }

      if (ignoredError instanceof RegExp) {
        return ignoredError.test(error.message);
      }
    });
  }
  return false;
}

export function init() {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT,

    beforeSend(event, hint) {
      const error = hint.originalException;
      if (isBrowserExtensionError(error)) {
        return null;
      }

      if (isIgnoredError(error)) {
        return null;
      }

      return event;
    },

    ignoreErrors: [
      "https://reactjs.org/docs/error-decoder.html?invariant=423", // There was an error while hydrating.
      /webpack:/, // Errors from outside libraries like axeptio
      /gmo/, // Can't find variable: gmo | Probably coming from axeptio
      /SnapTube/, // SnapTube error
      /static.axept.io/, // axeptio errors
    ],

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      // Replay is only available in the client
      Sentry.replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
        mask: ["[type=password]"],
      }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["winamp-web"],
        // Keep error, apply a `third_party_code: true` tag in case the error contains at least one third-party stack frame.
        behaviour: "apply-tag-if-contains-third-party-frames",
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,

    // Capture Replay for 0% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1.0,
  });

  function isBrowserExtensionError(exception: unknown): boolean {
    if (exception instanceof Error && exception.stack) {
      const extensionPattern =
        /chrome-extension:|moz-extension:|extensions|anonymous scripts/;
      return extensionPattern.test(exception.stack);
    }

    return false;
  }
}
